import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CustomIcons } from '@shared/models/custom-icons';
import { ActivatedRoute, Params } from '@angular/router';
import { Clipboard } from '@angular/cdk/clipboard';
import { DOCUMENT } from '@angular/common';
import { Select, Store } from '@ngxs/store';
import { takeUntil, tap } from 'rxjs/operators';
import { IDestroy, TakeUntilDestroy } from '@utils/auto-unsubscribe';
import { Observable, Subject } from 'rxjs';
import { SharedCatalogState } from '@shared/state/catalog/shared-catalog.state';
import { GeoDataInfo } from '@shared/components/map/models/map';
import { ActionsExecuting, actionsExecuting } from '@ngxs-labs/actions-executing';
import { CatalogItemFieldsVM } from '@shared/models/shared-catalog.state';
import { SnackBarService } from '@shared/components/snackbar-message/services/snackbar.service';
import { MirSnackbarType } from '@shared/components/snackbar-message/model/snackbar';
import { ShowLayout, ShowHeader } from '@shared/state/shared/shared.action';
import { BreakpointObserver } from '@angular/cdk/layout';
import { getLinkToObject } from '@utils/other-utils';
import { ReleaseStatus } from '@shared/models/catalog.service';
import { CatalogState } from '../../../../store/catalog/catalog.state';
import { DataRouting } from '../../../catalog-data/models/data-routing';
import { ChangeCatalogItem } from '../../store/catalog-items.actions';
import { GetSelectCatalogItem } from '../../../../store/catalog/catalog.action';
import { CatalogItemValue } from '../../../../models/catalog';
import { CatalogItemsQueryParams } from '../../models/catalog-item-component';

@Component({
  selector: 'app-catalog-item-card',
  templateUrl: './catalog-item-card.component.html',
  styleUrls: ['./catalog-item-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@TakeUntilDestroy
export class CatalogItemCardComponent implements OnInit, OnDestroy, IDestroy {
  public componentDestroy: () => Subject<any>;
  public showMap: boolean;
  @Output() public readonly closeCard = new EventEmitter<any>();
  @Output() public readonly backISelectedItem = new EventEmitter<any>();
  @Input()
  @HostBinding('class.fullscreen')
  public fullScreen = false;

  @Input() public isMap = false;
  @Input() public hasGeo?: boolean;
  @Input() public showGeoData = false;
  @Select(CatalogState.selectCatalogItem) public itemData$: Observable<CatalogItemValue>;
  @Select(SharedCatalogState.fields) public itemFields$: Observable<CatalogItemFieldsVM[]>;
  @Select(actionsExecuting([GetSelectCatalogItem])) public loadingData$: Observable<ActionsExecuting>;
  public icons = CustomIcons;
  public incorrectDataPage = `/opendata/${DataRouting.Appeals}`;
  public incorrectDataPageParams: Params;
  public mapGeoData: GeoDataInfo[] | null;
  public isDynamic = false;
  public isArchiveCatalog: boolean;
  public fileNameMap = new Map<string, { name: string; ext: string }>();

  public breakPointObserve$ = this.breakpointObserver.observe(['(max-width: 1024px)', '(max-width: 800px)']).pipe(
    tap(state => {
      if (state.breakpoints['(max-width: 1024px)']) {
        this.store.dispatch(new ShowLayout(false));
        this.document.documentElement.style.setProperty('--toolbar-height', '0px');
      } else {
        this.store.dispatch(new ShowLayout(true));
        this.document.documentElement.style.setProperty('--toolbar-height', '75px');
      }
      this.store.dispatch(new ShowHeader(!state.breakpoints['(max-width: 800px)']));
    })
  );
  public isNotPublicCatalog = false;
  constructor(
    private activeRoute: ActivatedRoute,
    private clipboard: Clipboard,
    @Inject(DOCUMENT) private document: Document,
    private store: Store,
    private snackBarService: SnackBarService,
    private breakpointObserver: BreakpointObserver,
  ) {}

  public ngOnInit(): void {
    const catalogVersion = this.store.selectSnapshot(CatalogState.currentCatalogVersionRelease);
    const selectCatalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
    this.isNotPublicCatalog = catalogVersion?.versionReleaseInfo?.releaseStatus === ReleaseStatus.New;
    if (this.hasGeo === undefined) {
      this.hasGeo = selectCatalog.hasGeo;
    }
    this.showGeoData = selectCatalog.hasGeo;

    this.isArchiveCatalog = selectCatalog.archive;
    const params = this.activeRoute.snapshot.queryParams as CatalogItemsQueryParams;

    if (params?.isDynamic && params.isDynamic === 'true') {
      this.isDynamic = true;
    }

    this.showMap = this.hasGeo;
    this.store
      .select(CatalogState.selectCatalogItem)
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe(res => {
        if (!res) {
          return;
        }

        this.mapGeoData = [{ center: res.geodata_center, allCoordinates: res.geoData, catalogItemId: res.global_id }];

        this.incorrectDataPageParams = {
          catalogItemGlobalId: res.global_id,
          catalogGlobalId: selectCatalog.id
        };
      });
  }

  ngOnDestroy(): void {
    this.store.dispatch(new ShowHeader(true));
  }

  public copyLinks(): void {
    this.snackBarService.open(MirSnackbarType.Success, [
      {
        translate: true,
        message: 'SNACK_BAR_MESSAGE.LINK_COPY'
      }
    ]);

    const objectLink = getLinkToObject(this.store, this.document, this.isMap);

    this.clipboard.copy(objectLink);
  }

  public closeItemCard(): void {
    if (!this.breakpointObserver.isMatched('(max-width: 1024px)')) {
      this.store.dispatch(new ShowLayout(true));
      this.document.documentElement.style.setProperty('--toolbar-height', '75px');
    }

    this.store.dispatch(new ChangeCatalogItem(null));
    this.closeCard.emit();
  }
}
